import { type ExcalidrawBinaryFiles, type ExcalidrawElement, excalidraw } from '../excalidraw';

/*
 * GitBook uses a custom SVG file to store drawing data.
 * It uses a SVG file so that the file can be rendered in GitHub or when downloaded by the user.
 */

/**
 * Document stored in the SVG.
 */
export interface DrawingDocumentData {
    elements: readonly ExcalidrawElement[];
    files: ExcalidrawBinaryFiles;
}

/**
 * Create a file from the SVG data.
 */
export function createDrawingFile(svg: string, fileName = 'file.excalidraw.svg'): File {
    return new File([svg], fileName, {
        type: 'image/svg+xml',
    });
}

/**
 * Create an SVG element from the drawing data.
 */
export async function createDrawingSVGElement(
    doc: DrawingDocumentData,
    options: {
        embedScene?: boolean;
        theme?: 'light' | 'dark';
    } = {}
): Promise<SVGSVGElement> {
    const { embedScene = true, theme = 'light' } = options;
    const {
        default: { exportToSvg },
    } = await excalidraw();

    const svg = await exportToSvg({
        appState: {
            viewBackgroundColor: 'transparent',
            exportEmbedScene: embedScene,
            exportWithDarkMode: theme === 'dark',
        },
        elements: doc.elements,
        files: doc.files,
        exportPadding: 30,
    });

    // Embedding fonts directly in the SVG ensures it remains self-contained when rendered as an image.
    // This is a temporary workaround because Excalidraw does not yet embed fonts in the SVG by default.
    // Although Excalidraw has resolved this issue, the fix is not available in the current package version.
    // Relevant links:
    // - Excalidraw issue discussion: https://github.com/excalidraw/excalidraw/issues/1972#issuecomment-2417744618
    // - Gitbook PR addressing the issue: https://github.com/GitbookIO/gitbook-x/pull/15594
    const styleFonts = svg.querySelector('.style-fonts');
    if (styleFonts) {
        styleFonts.innerHTML = await import('./font').then(
            (module) => module.embeddableExcalidrawFonts
        );
    }

    return svg;
}

/**
 * Create the SVG file to be saved with the drawing data.
 */
export async function createDrawingSVG(doc: DrawingDocumentData): Promise<string> {
    const svg = await createDrawingSVGElement(doc);
    return new XMLSerializer().serializeToString(svg);
}

/**
 * Create the drawing data for a mermaid diagram.
 */
export async function createDrawingFromMermaid(
    mermaid: string
): Promise<DrawingDocumentData | null> {
    const {
        default: { parseMermaidToExcalidraw, convertToExcalidrawElements },
    } = await excalidraw();

    try {
        const { elements, files } = await parseMermaidToExcalidraw(mermaid, {
            themeVariables: {
                fontSize: '20px',
            },
        });
        const doc: DrawingDocumentData = {
            elements: convertToExcalidrawElements(elements),
            files: files ?? {},
        };

        return doc;
    } catch (_err) {
        return null;
    }
}

/**
 * Create the SVG file for a mermaid diagram.
 */
export async function createDrawingSVGFromMermaid(mermaid: string): Promise<string | null> {
    const doc = await createDrawingFromMermaid(mermaid);
    if (!doc) {
        return null;
    }

    return createDrawingSVG(doc);
}
